import Card from '@pelckmans/elementis/components/card';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SharedUserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import IdentityContext from '../../../../../contexts/IdentityContext';
import ModalContext from '../../../../../contexts/ModalContext';
import { AssignmentDeadlinePill } from '../../../../../shared-components/AssignmentDeadlinePill';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MaterialIcon } from '../../../components/MaterialIcon';
import useSlideSets from '../../../hooks/useSlideSets';
import MedialinksContext from '../../_contexts/MedialinksContext';
import type { PinnedMaterial } from '../../_contexts/PinnedMaterialContext';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import useMedialinks from '../../hooks/medialink/useMedialinks';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';
import MaterialPills from '../MaterialPills';
import ScorableProgress from '../ScorableProgress';

export default function PinnedMaterialCard({ material }: { material: PinnedMaterial }) {
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);

  const { medialinks } = useContext(MedialinksContext);
  const { openMedialink } = useMedialinks();

  const { userMaterial, fetchUserMaterial } = useContext(UserMaterialContext);
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { openMaterial: openUserMaterial } = useUserMaterial();

  const { buildDoceoUrl, captureOpenSlideSetEvents } = useSlideSets();

  const subLocationId = selectedModule?.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_PINNED_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_PINNED_MATERIAL;

  function getIconText() {
    if (material.deadline && !identity.isTeacherAlike) {
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.ASSIGNMENT');
    } else if (material.type === 'user-material') {
      return identity.isTeacherAlike
        ? t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.OWN_MATERIAL')
        : t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SHARED_USER_MATERIAL');
    } else if (material.type === 'slide-set') {
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SLIDE_SET');
    } else {
      return material.contentType?.name;
    }
  }

  function getTags() {
    const medialink = medialinks.find(x => x.id === material.id);
    const isAdaptive = medialink?.assessmentQExercise?.isAdaptive;
    const isOnTheFly = medialink?.assessmentQExercise?.isOnTheFly;

    if (identity.isTeacherAlike) {
      return buildGroupProps(
        <MaterialPills
          isAssignment={material.isAssignment}
          isShared={material.isShared}
          isStudent={material.group === 'student'}
          isTeacher={material.group === 'teacher'}
          isAdaptive={isAdaptive}
          isOnTheFly={isOnTheFly}
        />,
      );
    }

    return buildGroupProps(
      <>
        {material.deadline && <AssignmentDeadlinePill deadline={material.deadline} key="assignment-deadline-pill" />}
        <MaterialPills isAdaptive={isAdaptive} isOnTheFly={isOnTheFly} key="material-pills" />
      </>,
    );
  }

  if (material.type === 'slide-set') {
    return (
      <Card
        titleText={material.name}
        titleTag="h3"
        as="link"
        icon={<MaterialIcon materialType="slide-set" isUserSlideSet={material.isUserSlideSet} />}
        iconText={getIconText()}
        tags={getTags()}
        href={buildDoceoUrl({ ...material, id: Number(material.id) })}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => captureOpenSlideSetEvents(Number(material.id), subLocationId)}
      />
    );
  }

  async function handleClickMaterial() {
    if (!selectedNode || !selectedModule) return;

    if (material.type === 'medialink') {
      const medialink = medialinks.find(x => x.id === material.id);

      if (medialink) {
        const modal = await openMedialink(medialink, selectedNode, selectedModule, undefined, undefined, subLocationId);

        if (modal) {
          showModal(modal);
        }
      }
    }

    if (material.type === 'user-material') {
      const foundUserMaterial = userMaterial.find(x => x.id === material.id);
      const foundSharedMaterial = sharedMaterials.find(x => x.id === material.id) as SharedUserMaterial;
      const foundMaterial = foundUserMaterial ?? foundSharedMaterial;

      if (foundMaterial) {
        const modal = await openUserMaterial(
          foundMaterial,
          nodes,
          foundMaterial.userMaxScore ? fetchUserMaterial : undefined,
          subLocationId,
        );
        if (modal) showModal(modal);
      }
    }
  }

  // logic for non-embedded links is handled in the PinnedMaterialContext
  if (material.href) {
    return (
      <Card
        as="link"
        href={material.href}
        target="_blank"
        rel="noopener noreferrer"
        titleText={material.name}
        titleTag="h3"
        onClick={() => handleClickMaterial()}
        icon={<MaterialIcon materialType={material.type} contentType={material.contentType} />}
        iconText={getIconText()}
        tags={getTags()}
      />
    );
  }

  return (
    <Card
      as="button"
      titleText={material.name}
      titleTag="h3"
      onClick={handleClickMaterial}
      icon={<MaterialIcon materialType={material.type} contentType={material.contentType} />}
      iconText={getIconText()}
      tags={getTags()}
      results={material.score && <ScorableProgress score={material.score} />}
    />
  );
}
